import React from 'react';
import tw from 'twin.macro';
import { Link } from 'gatsby';
import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import ReactDOMServer from 'react-dom/server';

export interface SearchItemProps {
  style?: React.CSSProperties;
  className?: string;
  title?: string;
  date?: string;
  info?: string;
  image?: string;
  link?: string;
  category?: string;
  description?: string;
}

const articleImageStyle = css`
  ${tw`flex-none mr-4 rounded-lg`}
  height: 124px;
  width: 124px;
  @media (max-width: 1024px) {
    height: 112px;
    width: 112px;
  }
`;

const SearchItem: React.FC<SearchItemProps> = ({
  description,
  style,
  className,
  title,
  date,
  info,
  image,
  link,
  category,
}) => {
  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => {
        return <React.Fragment>{children}</React.Fragment>;
      },
    },
  };
  const renderDescription = description
    ? documentToReactComponents(JSON.parse(description), options)
    : null;
  return (
    <div style={style} className={className} tw="flex mb-4">
      <Link to={link} target="__blank">
        {image ? (
          <div
            tw="flex-none mr-4 rounded-lg"
            css={css`
              height: 124px;
              width: 124px;
              background: url(${image});
              background-repeat: no-repeat;
              background-size: cover;
              @media (max-width: 1024px) {
                height: 112px;
                width: 112px;
              }
            `}
          ></div>
        ) : (
          <div css={articleImageStyle}></div>
        )}
      </Link>
      <div tw="text-left">
        <div tw="text-b3 py-1 px-3 rounded-full bg-blue-sky w-max mb-2">
          {category ? (
            <span
              dangerouslySetInnerHTML={{
                __html: category
                  .replace(new RegExp('-::', 'g'), '<b>')
                  .replace(new RegExp('::-', 'g'), '</b>'),
              }}
            ></span>
          ) : null}
        </div>
        <div tw="mb-2">
          <Link
            tw="text-black-100 dark:text-white cursor-pointer no-underline"
            to={link}
            target="__blank"
          >
            <div tw="mb-2 text-h4 font-extrabold font-gilroy">
              <span
                dangerouslySetInnerHTML={{
                  __html: title
                    .replace(new RegExp('-::', 'g'), '<b>')
                    .replace(new RegExp('::-', 'g'), '</b>'),
                }}
              ></span>
            </div>
          </Link>
        </div>
        <div
          tw="text-b3 mb-2 dark:text-black-700"
          css={css`
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            p {
              margin: 0;
            }
          `}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: ReactDOMServer.renderToString(renderDescription as React.ReactElement)
                .replace(new RegExp('-::', 'g'), '<b>')
                .replace(new RegExp('::-', 'g'), '</b>'),
            }}
          ></div>
        </div>
        <p tw="text-b3 text-black-300 dark:text-black-700 m-0">
          <span>
            {DateTime.fromJSDate(new Date(date)).toFormat('dd MMMM yyyy', { locale: 'id' })}
          </span>
          <span tw="px-1"> • </span>
          <span>{info}</span>
        </p>
      </div>
    </div>
  );
};

export default SearchItem;
